import { useState, useEffect, useCallback } from 'react';
import { testAdminApi } from '../api';

// ── helpers ──────────────────────────────────────────────────────────────────

function gradeColor(grade) {
  if (!grade) return {};
  const g = grade.toUpperCase();
  if (g === 'A+') return { background: '#fef3c7', color: '#92400e', fontWeight: 700 };
  if (g === 'A')  return { background: '#f0fff4', color: '#22863a', fontWeight: 700 };
  if (g === 'B')  return { background: '#ebf8ff', color: '#0066CC', fontWeight: 700 };
  if (g === 'C')  return { background: '#fffaf0', color: '#c05621', fontWeight: 700 };
  return { background: '#fff5f5', color: '#c53030', fontWeight: 700 };
}

function statusBadgeClass(status) {
  if (status === 'RELEASED') return 'badge badge-green';
  if (status === 'CLOSED')   return 'badge badge-gray';
  return 'badge badge-orange'; // DRAFT
}

function truncate(str, n) {
  if (!str) return '';
  return str.length > n ? str.slice(0, n) + '…' : str;
}

// ── EMPTY FORMS ───────────────────────────────────────────────────────────────

const EMPTY_SUBJECT = { name: '', description: '', className: '', isActive: true };
const EMPTY_QUESTION = {
  subjectId: '', questionText: '', optionA: '', optionB: '', optionC: '', optionD: '',
  correctOption: 'A', marks: 1, difficulty: 'MEDIUM',
};
const EMPTY_TEST = {
  name: '', subjectId: '', description: '', className: '', numQuestions: 10,
  timeLimitMinutes: 30, marksPerQuestion: 1, passingPercentage: 40,
  shuffleQuestions: false, showResultImmediately: true, allowRetake: false,
};

// ── CLASSES TAB ───────────────────────────────────────────────────────────────

const EMPTY_CLASS = { name: '', section: '', description: '', isActive: true };

function ClassesTab() {
  const [classes, setClasses] = useState([]);
  const [form, setForm] = useState(EMPTY_CLASS);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    testAdminApi.getClasses()
      .then(setClasses)
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { setErr('Class name is required'); return; }
    setErr('');
    setSaving(true);
    try {
      if (editId) await testAdminApi.updateClass(editId, form);
      else await testAdminApi.saveClass(form);
      window.location.reload();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleEdit = (c) => {
    setForm({ name: c.name, section: c.section || '', description: c.description || '', isActive: !!c.isActive });
    setEditId(c.id);
    setErr('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this class?')) return;
    try {
      await testAdminApi.deleteClass(id);
      window.location.reload();
    } catch (e) { setErr(e.message); }
  };

  const handleCancel = () => { setForm(EMPTY_CLASS); setEditId(null); setErr(''); };

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      {/* Add/Edit Form */}
      <div className="card" style={{ flex: '0 0 300px', minWidth: 260 }}>
        <div className="card-header">
          <div className="card-title">🏫 {editId ? 'Edit Class' : 'Add Class'}</div>
        </div>
        <div className="card-body">
          {err && <div className="error-msg">{err}</div>}
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label className="form-label">🏫 Class Name *</label>
            <input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Class 1" />
          </div>
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label className="form-label">🔤 Section</label>
            <input className="form-control" value={form.section} onChange={e => set('section', e.target.value)} placeholder="e.g. A (optional)" />
          </div>
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label className="form-label">📝 Description</label>
            <textarea className="form-control" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Optional" style={{ minHeight: 60 }} />
          </div>
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#4a5568' }}>
              <input type="checkbox" checked={form.isActive} onChange={e => set('isActive', e.target.checked)} />
              Active
            </label>
          </div>
          <div className="form-actions">
            {editId && <button className="btn btn-outline btn-sm" onClick={handleCancel}>✖️ Cancel</button>}
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
              💾 {editId ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ flex: 1, minWidth: 280 }}>
        <div className="card-header">
          <div className="card-title">🏫 Classes ({classes.length})</div>
        </div>
        {loading ? <div className="loading-msg">Loading…</div> : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>#</th><th>🏫 Name</th><th>🔤 Section</th><th>📝 Description</th><th>🔘 Active</th><th>⚙️ Actions</th></tr>
              </thead>
              <tbody>
                {classes.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No classes yet. Add one to get started.</td></tr>
                )}
                {classes.map((c, i) => (
                  <tr key={c.id}>
                    <td>{i + 1}</td>
                    <td><strong>{c.name}</strong></td>
                    <td>{c.section || <span style={{ color: '#a0aec0' }}>—</span>}</td>
                    <td style={{ color: '#718096' }}>{truncate(c.description, 60) || '—'}</td>
                    <td>
                      <span className={`badge ${c.isActive ? 'badge-green' : 'badge-red'}`}>
                        {c.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-outline btn-sm" onClick={() => handleEdit(c)} title="Edit">✏️ Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c.id)} title="Delete">🗑️ Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── SUBJECTS TAB ──────────────────────────────────────────────────────────────

function SubjectsTab({ classes }) {
  const [subjects, setSubjects] = useState([]);
  const [form, setForm] = useState(EMPTY_SUBJECT);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    testAdminApi.getSubjects()
      .then(setSubjects)
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { setErr('Subject name is required'); return; }
    setErr('');
    setSaving(true);
    try {
      if (editId) await testAdminApi.updateSubject(editId, form);
      else await testAdminApi.saveSubject(form);
      window.location.reload();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleEdit = (s) => {
    setForm({ name: s.name, description: s.description || '', className: s.className || '', isActive: !!s.isActive });
    setEditId(s.id);
    setErr('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this subject?')) return;
    try {
      await testAdminApi.deleteSubject(id);
      window.location.reload();
    } catch (e) { setErr(e.message); }
  };

  const handleCancel = () => { setForm(EMPTY_SUBJECT); setEditId(null); setErr(''); };

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      {/* Add/Edit Form */}
      <div className="card" style={{ flex: '0 0 300px', minWidth: 260 }}>
        <div className="card-header">
          <div className="card-title"><i className="fas fa-book" style={{marginRight:6,color:'#0066CC'}}></i>{editId ? 'Edit Subject' : 'Add Subject'}</div>
        </div>
        <div className="card-body">
          {err && <div className="error-msg">{err}</div>}
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label className="form-label">📖 Name *</label>
            <input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Mathematics" />
          </div>
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label className="form-label">📝 Description</label>
            <textarea className="form-control" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Optional description" style={{ minHeight: 70 }} />
          </div>
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label className="form-label">🏫 Class *</label>
            <select className="form-control" value={form.className} onChange={e => set('className', e.target.value)} required>
              <option value="">-- Select Class --</option>
              {classes.map(c => {
                const label = c.section ? `${c.name} - ${c.section}` : c.name;
                return <option key={c.id} value={label}>{label}</option>;
              })}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#4a5568' }}>
              <input type="checkbox" checked={form.isActive} onChange={e => set('isActive', e.target.checked)} />
              Active
            </label>
          </div>
          <div className="form-actions">
            {editId && <button className="btn btn-outline btn-sm" onClick={handleCancel}>✖️ Cancel</button>}
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
              💾 {editId ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ flex: 1, minWidth: 280 }}>
        <div className="card-header">
          <div className="card-title"><i className="fas fa-list" style={{marginRight:6,color:'#0066CC'}}></i>Subjects ({subjects.length})</div>
        </div>
        {loading ? <div className="loading-msg">Loading…</div> : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>#</th><th>📖 Name</th><th>📝 Description</th><th>🏫 Class</th><th>🔘 Active</th><th>⚙️ Actions</th></tr>
              </thead>
              <tbody>
                {subjects.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No subjects yet</td></tr>
                )}
                {subjects.map((s, i) => (
                  <tr key={s.id}>
                    <td>{i + 1}</td>
                    <td><strong>{s.name}</strong></td>
                    <td style={{ color: '#718096' }}>{truncate(s.description, 60) || '—'}</td>
                    <td>{s.className || '—'}</td>
                    <td>
                      <span className={`badge ${s.isActive ? 'badge-green' : 'badge-red'}`}>
                        {s.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-outline btn-sm" onClick={() => handleEdit(s)} title="Edit">✏️ Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(s.id)} title="Delete">🗑️ Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── QUESTIONS TAB ─────────────────────────────────────────────────────────────

function QuestionsTab({ subjects, classes }) {
  const [questions, setQuestions] = useState([]);
  const [form, setForm] = useState(EMPTY_QUESTION);
  const [formClass, setFormClass] = useState('');
  const [editId, setEditId] = useState(null);
  const [filterClass, setFilterClass] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterDiff, setFilterDiff] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    testAdminApi.getQuestions()
      .then(setQuestions)
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const classLabel = (c) => c.section ? `${c.name} - ${c.section}` : c.name;

  const handleFormClassChange = (cls) => {
    setFormClass(cls);
    set('subjectId', '');
  };

  const formSubjects = formClass
    ? subjects.filter(s => s.className === formClass)
    : subjects;

  const filtered = questions.filter(q => {
    const matchClass = !filterClass || q.className === filterClass;
    const matchSubject = !filterSubject || String(q.subjectId) === String(filterSubject);
    const matchDiff = !filterDiff || q.difficulty === filterDiff;
    const matchSearch = !search || (q.questionText || '').toLowerCase().includes(search.toLowerCase());
    return matchClass && matchSubject && matchDiff && matchSearch;
  });

  const subjectName = (id) => {
    const s = subjects.find(s => String(s.id) === String(id));
    return s ? s.name : '—';
  };

  const handleSave = async () => {
    if (!form.subjectId || !form.questionText.trim()) { setErr('Subject and question text are required'); return; }
    setErr('');
    setSaving(true);
    try {
      if (editId) await testAdminApi.updateQuestion(editId, form);
      else await testAdminApi.saveQuestion(form);
      window.location.reload();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleEdit = (q) => {
    setFormClass(q.className || '');
    setForm({
      subjectId: q.subjectId || '',
      questionText: q.questionText || '',
      optionA: q.optionA || '',
      optionB: q.optionB || '',
      optionC: q.optionC || '',
      optionD: q.optionD || '',
      correctOption: q.correctOption || 'A',
      marks: q.marks || 1,
      difficulty: q.difficulty || 'MEDIUM',
    });
    setEditId(q.id);
    setShowForm(true);
    setErr('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await testAdminApi.deleteQuestion(id);
      window.location.reload();
    } catch (e) { setErr(e.message); }
  };

  const handleCancel = () => { setFormClass(''); setForm(EMPTY_QUESTION); setEditId(null); setShowForm(false); setErr(''); };

  return (
    <div>
      {/* Filter bar */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '12px 20px' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <select className="form-control" style={{ width: 160 }} value={filterClass} onChange={e => { setFilterClass(e.target.value); setFilterSubject(''); }}>
              <option value="">All Classes</option>
              {classes.map(c => <option key={c.id} value={classLabel(c)}>{classLabel(c)}</option>)}
            </select>
            <select className="form-control" style={{ width: 160 }} value={filterSubject} onChange={e => setFilterSubject(e.target.value)}>
              <option value="">All Subjects</option>
              {(filterClass ? subjects.filter(s => s.className === filterClass) : subjects).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select className="form-control" style={{ width: 140 }} value={filterDiff} onChange={e => setFilterDiff(e.target.value)}>
              <option value="">All Difficulties</option>
              <option value="EASY">Easy</option>
              <option value="MEDIUM">Medium</option>
              <option value="HARD">Hard</option>
            </select>
            <div className="search-bar">
              <i className="fas fa-search"></i>
              <input type="text" placeholder="Search questions…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => { setShowForm(true); setEditId(null); setForm(EMPTY_QUESTION); setErr(''); }}>
              ➕ Add Question
            </button>
          </div>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <div className="card-title"><i className="fas fa-question-circle" style={{marginRight:6,color:'#0066CC'}}></i>{editId ? 'Edit Question' : 'Add Question'}</div>
          </div>
          <div className="card-body">
            {err && <div className="error-msg">{err}</div>}
            <div className="form-grid cols-2" style={{ marginBottom: 10 }}>
              <div className="form-group">
                <label className="form-label">🏫 Class</label>
                <select className="form-control" value={formClass} onChange={e => handleFormClassChange(e.target.value)}>
                  <option value="">-- Select Class --</option>
                  {classes.map(c => <option key={c.id} value={classLabel(c)}>{classLabel(c)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">📖 Subject *</label>
                <select className="form-control" value={form.subjectId} onChange={e => set('subjectId', e.target.value)}>
                  <option value="">-- Select Subject --</option>
                  {formSubjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">🔥 Difficulty</label>
                <select className="form-control" value={form.difficulty} onChange={e => set('difficulty', e.target.value)}>
                  <option value="EASY">Easy</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HARD">Hard</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">🏅 Marks</label>
                <input className="form-control" type="number" min={1} value={form.marks} onChange={e => set('marks', parseInt(e.target.value) || 1)} />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label className="form-label">❓ Question Text *</label>
              <textarea className="form-control" value={form.questionText} onChange={e => set('questionText', e.target.value)} placeholder="Enter the question…" style={{ minHeight: 80 }} />
            </div>
            <div className="form-grid cols-2" style={{ marginBottom: 10 }}>
              <div className="form-group">
                <label className="form-label">Option A</label>
                <input className="form-control" value={form.optionA} onChange={e => set('optionA', e.target.value)} placeholder="Option A" />
              </div>
              <div className="form-group">
                <label className="form-label">Option B</label>
                <input className="form-control" value={form.optionB} onChange={e => set('optionB', e.target.value)} placeholder="Option B" />
              </div>
              <div className="form-group">
                <label className="form-label">Option C</label>
                <input className="form-control" value={form.optionC} onChange={e => set('optionC', e.target.value)} placeholder="Option C" />
              </div>
              <div className="form-group">
                <label className="form-label">Option D</label>
                <input className="form-control" value={form.optionD} onChange={e => set('optionD', e.target.value)} placeholder="Option D" />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 14, maxWidth: 200 }}>
              <label className="form-label">Correct Option</label>
              <select className="form-control" value={form.correctOption} onChange={e => set('correctOption', e.target.value)}>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
                <option value="D">D</option>
              </select>
            </div>
            <div className="form-actions">
              <button className="btn btn-outline btn-sm" onClick={handleCancel}>✖️ Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                💾 {editId ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card">
        <div className="card-header">
          <div className="card-title"><i className="fas fa-question-circle" style={{marginRight:6,color:'#0066CC'}}></i>Questions ({filtered.length})</div>
        </div>
        {loading ? <div className="loading-msg">Loading…</div> : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>#</th><th>📖 Subject</th><th>🏫 Class</th><th>❓ Question</th><th>✅ Correct</th><th>🏅 Marks</th><th>🔥 Difficulty</th><th>⚙️ Actions</th></tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No questions found</td></tr>
                )}
                {filtered.map((q, i) => (
                  <tr key={q.id}>
                    <td>{i + 1}</td>
                    <td><span className="badge badge-blue">{subjectName(q.subjectId)}</span></td>
                    <td>{q.className || '—'}</td>
                    <td style={{ maxWidth: 320 }}>{truncate(q.questionText, 80)}</td>
                    <td><strong>{q.correctOption}</strong></td>
                    <td>{q.marks}</td>
                    <td>
                      <span className={`badge ${q.difficulty === 'EASY' ? 'badge-green' : q.difficulty === 'HARD' ? 'badge-red' : 'badge-orange'}`}>
                        {q.difficulty}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-outline btn-sm" onClick={() => handleEdit(q)} title="Edit">✏️ Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(q.id)} title="Delete">🗑️ Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── GRADE SETTINGS PANEL ──────────────────────────────────────────────────────

function GradeSettingsPanel({ test, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    testAdminApi.getGrades(test.id)
      .then(data => setRows(Array.isArray(data) ? data : []))
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [test.id]);

  const addRow = () => setRows(r => [...r, { gradeLetter: '', minPercentage: '', maxPercentage: '' }]);
  const removeRow = (i) => setRows(r => r.filter((_, idx) => idx !== i));
  const setRow = (i, k, v) => setRows(r => r.map((row, idx) => idx === i ? { ...row, [k]: v } : row));

  const handleSave = async () => {
    setErr('');
    setSaving(true);
    try {
      await testAdminApi.saveGrades(test.id, rows);
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="card" style={{ marginTop: 16, border: '2px solid #4CCEE8' }}>
      <div className="card-header" style={{ background: '#ebf8ff' }}>
        <div className="card-title" style={{ color: '#0066CC' }}>
          <i className="fas fa-star" style={{ marginRight: 8 }}></i>
          Grade Settings for: {test.name}
        </div>
        <button className="btn btn-outline btn-sm" onClick={onClose}>✖️ Close</button>
      </div>
      <div className="card-body">
        {err && <div className="error-msg">{err}</div>}
        {loading ? <div className="loading-msg">Loading grades…</div> : (
          <>
            <div className="table-wrap" style={{ marginBottom: 12 }}>
              <table>
                <thead>
                  <tr><th>Grade Letter</th><th>Min %</th><th>Max %</th><th></th></tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={4} style={{ textAlign: 'center', color: '#888', padding: 16 }}>No grade rows. Click "Add Grade Row" to start.</td></tr>
                  )}
                  {rows.map((row, i) => (
                    <tr key={i}>
                      <td>
                        <input className="form-control" style={{ width: 80 }} value={row.gradeLetter} onChange={e => setRow(i, 'gradeLetter', e.target.value)} placeholder="A+" />
                      </td>
                      <td>
                        <input className="form-control" style={{ width: 80 }} type="number" min={0} max={100} value={row.minPercentage} onChange={e => setRow(i, 'minPercentage', e.target.value)} placeholder="0" />
                      </td>
                      <td>
                        <input className="form-control" style={{ width: 80 }} type="number" min={0} max={100} value={row.maxPercentage} onChange={e => setRow(i, 'maxPercentage', e.target.value)} placeholder="100" />
                      </td>
                      <td>
                        <button className="btn btn-danger btn-sm" onClick={() => removeRow(i)} title="Remove">➖</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-outline btn-sm" onClick={addRow}>➕ Add Grade Row</button>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>💾 Save Grades</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── QUESTION PREVIEW PANEL ────────────────────────────────────────────────────

function QuestionPreviewPanel({ test, onClose }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    testAdminApi.getQuestions(test.subjectId)
      .then(data => setQuestions(Array.isArray(data) ? data : []))
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [test.subjectId]);

  const diffClass = d => d === 'EASY' ? 'badge-green' : d === 'HARD' ? 'badge-red' : 'badge-orange';

  return (
    <div className="card" style={{ marginTop: 16, border: '2px solid #0066CC' }}>
      <div className="card-header" style={{ background: '#ebf8ff' }}>
        <div className="card-title" style={{ color: '#0066CC' }}>
          <i className="fas fa-eye" style={{ marginRight: 8 }}></i>
          Question Pool for "{test.name}" — {questions.length} questions available, {test.numQuestions} will be selected per student
        </div>
        <button className="btn btn-outline btn-sm" onClick={onClose}>✖️ Close</button>
      </div>
      <div className="card-body" style={{ padding: 0, maxHeight: 360, overflowY: 'auto' }}>
        {err && <div className="error-msg" style={{ margin: 16 }}>{err}</div>}
        {loading ? <div className="loading-msg">Loading questions…</div> : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>#</th><th>Question</th><th>Correct</th><th>Difficulty</th></tr>
              </thead>
              <tbody>
                {questions.length === 0 && (
                  <tr><td colSpan={4} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No questions found for this subject</td></tr>
                )}
                {questions.map((q, i) => (
                  <tr key={q.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{i + 1}</td>
                    <td style={{ maxWidth: 400 }}>{truncate(q.questionText, 100)}</td>
                    <td><strong>{q.correctOption}</strong></td>
                    <td><span className={`badge ${diffClass(q.difficulty)}`}>{q.difficulty}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── TESTS TAB ─────────────────────────────────────────────────────────────────

function TestsTab({ subjects, classes }) {
  const [tests, setTests] = useState([]);
  const [form, setForm] = useState(EMPTY_TEST);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [gradeTest, setGradeTest] = useState(null);
  const [previewTest, setPreviewTest] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    testAdminApi.getTests()
      .then(setTests)
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const subjectName = (id) => {
    const s = subjects.find(s => String(s.id) === String(id));
    return s ? s.name : '—';
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.subjectId) { setErr('Name and subject are required'); return; }
    setErr('');
    setSaving(true);
    try {
      if (editId) await testAdminApi.updateTest(editId, form);
      else await testAdminApi.saveTest(form);
      window.location.reload();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleEdit = (t) => {
    setForm({
      name: t.name || '',
      subjectId: t.subjectId || '',
      description: t.description || '',
      className: t.className || '',
      numQuestions: t.numQuestions || 10,
      timeLimitMinutes: t.timeLimitMinutes || 30,
      marksPerQuestion: t.marksPerQuestion || 1,
      passingPercentage: t.passingPercentage || 40,
      shuffleQuestions: t.shuffleQuestions || false,
      showResultImmediately: !!t.showResultImmediately,
      allowRetake: t.allowRetake || false,
    });
    setEditId(t.id);
    setErr('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this test?')) return;
    try {
      await testAdminApi.deleteTest(id);
      window.location.reload();
    } catch (e) { setErr(e.message); }
  };

  const handleRelease = async (t) => {
    if (!window.confirm(`Release "${t.name}"? Students will be able to see and take this test.`)) return;
    try {
      await testAdminApi.releaseTest(t.id);
      window.location.reload();
    } catch (e) { setErr(e.message); }
  };

  const handleClose = async (t) => {
    if (!window.confirm(`Close "${t.name}"? No new attempts will be allowed.`)) return;
    try {
      await testAdminApi.closeTest(t.id);
      window.location.reload();
    } catch (e) { setErr(e.message); }
  };

  const handleCancel = () => { setForm(EMPTY_TEST); setEditId(null); setErr(''); };

  return (
    <div>
      {err && <div className="error-msg">{err}</div>}
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Test list */}
        <div style={{ flex: 2, minWidth: 320 }}>
          <div className="card">
            <div className="card-header">
              <div className="card-title"><i className="fas fa-tasks" style={{marginRight:6,color:'#0066CC'}}></i>Tests ({tests.length})</div>
            </div>
            {loading ? <div className="loading-msg">Loading…</div> : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>📝 Name</th><th>📖 Subject</th><th>🏫 Class</th><th>❓ Qs</th><th>⏱️ Time</th><th>🔘 Status</th><th>⚙️ Actions</th></tr>
                  </thead>
                  <tbody>
                    {tests.length === 0 && (
                      <tr><td colSpan={7} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No tests yet</td></tr>
                    )}
                    {tests.map(t => (
                      <tr key={t.id}>
                        <td><strong>{t.name}</strong></td>
                        <td>{subjectName(t.subjectId)}</td>
                        <td>{t.className || '—'}</td>
                        <td>{t.numQuestions}</td>
                        <td>{t.timeLimitMinutes} min</td>
                        <td><span className={statusBadgeClass(t.status)}>{t.status || 'DRAFT'}</span></td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <button className="btn btn-outline btn-sm" onClick={() => handleEdit(t)} title="Edit">✏️ Edit</button>
                            <button className="btn btn-danger btn-sm" onClick={() => handleDelete(t.id)} title="Delete">🗑️ Delete</button>
                            {(!t.status || t.status === 'DRAFT') && (
                              <button className="btn btn-success btn-sm" onClick={() => handleRelease(t)} title="Release">
                                🚀 Release
                              </button>
                            )}
                            {t.status === 'RELEASED' && (
                              <button className="btn btn-warning btn-sm" onClick={() => handleClose(t)} title="Close">
                                🔒 Close
                              </button>
                            )}
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={() => setGradeTest(gradeTest?.id === t.id ? null : t)}
                              title="Grade Settings"
                              style={gradeTest?.id === t.id ? { background: '#ebf8ff', borderColor: '#0066CC', color: '#0066CC' } : {}}
                            >
                              ⭐ Grades
                            </button>
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={() => setPreviewTest(previewTest?.id === t.id ? null : t)}
                              title="Preview Questions"
                              style={previewTest?.id === t.id ? { background: '#ebf8ff', borderColor: '#0066CC', color: '#0066CC' } : {}}
                            >
                              👁️ Preview
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Grade settings panel */}
          {gradeTest && (
            <GradeSettingsPanel test={gradeTest} onClose={() => setGradeTest(null)} />
          )}

          {/* Question preview panel */}
          {previewTest && (
            <QuestionPreviewPanel test={previewTest} onClose={() => setPreviewTest(null)} />
          )}
        </div>

        {/* Create/Edit Form */}
        <div className="card" style={{ flex: '0 0 300px', minWidth: 280 }}>
          <div className="card-header">
            <div className="card-title"><i className="fas fa-plus-circle" style={{marginRight:6,color:'#0066CC'}}></i>{editId ? 'Edit Test' : 'Create Test'}</div>
          </div>
          <div className="card-body">
            {err && !loading && <div className="error-msg">{err}</div>}
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label className="form-label">📝 Test Name *</label>
              <input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Mid-Term Math" />
            </div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label className="form-label">📖 Subject *</label>
              <select className="form-control" value={form.subjectId} onChange={e => set('subjectId', e.target.value)}>
                <option value="">-- Select Subject --</option>
                {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label className="form-label">📝 Description</label>
              <textarea className="form-control" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Optional" style={{ minHeight: 60 }} />
            </div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label className="form-label">🏫 Class *</label>
              <select className="form-control" value={form.className} onChange={e => set('className', e.target.value)} required>
                <option value="">-- Select Class --</option>
                {classes.map(c => {
                  const label = c.section ? `${c.name} - ${c.section}` : c.name;
                  return <option key={c.id} value={label}>{label}</option>;
                })}
              </select>
            </div>
            <div className="form-grid cols-2" style={{ marginBottom: 10 }}>
              <div className="form-group">
                <label className="form-label">No. of Questions</label>
                <input className="form-control" type="number" min={1} value={form.numQuestions} onChange={e => set('numQuestions', parseInt(e.target.value) || 1)} />
              </div>
              <div className="form-group">
                <label className="form-label">Time (minutes)</label>
                <input className="form-control" type="number" min={1} value={form.timeLimitMinutes} onChange={e => set('timeLimitMinutes', parseInt(e.target.value) || 1)} />
              </div>
              <div className="form-group">
                <label className="form-label">🏅 Marks/Question</label>
                <input className="form-control" type="number" min={1} value={form.marksPerQuestion} onChange={e => set('marksPerQuestion', parseInt(e.target.value) || 1)} />
              </div>
              <div className="form-group">
                <label className="form-label">Passing % (0-100)</label>
                <input className="form-control" type="number" min={0} max={100} value={form.passingPercentage} onChange={e => set('passingPercentage', parseInt(e.target.value) || 0)} />
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
              {[
                ['shuffleQuestions', 'Shuffle Questions'],
                ['showResultImmediately', 'Show Result Immediately'],
                ['allowRetake', 'Allow Retake'],
              ].map(([k, label]) => (
                <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#4a5568', fontWeight: 500 }}>
                  <input type="checkbox" checked={form[k]} onChange={e => set(k, e.target.checked)} />
                  {label}
                </label>
              ))}
            </div>
            <div className="form-actions">
              {editId && <button className="btn btn-outline btn-sm" onClick={handleCancel}>✖️ Cancel</button>}
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                💾 {editId ? 'Update Test' : 'Save Test'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── RESULTS TAB ───────────────────────────────────────────────────────────────

function ResultsTab({ subjects }) {
  const [tests, setTests] = useState([]);
  const [results, setResults] = useState([]);
  const [filterTest, setFilterTest] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErr, setDetailErr] = useState('');
  const [openAttemptId, setOpenAttemptId] = useState(null);

  useEffect(() => {
    testAdminApi.getTests()
      .then(setTests)
      .catch(e => setErr(e.message));
  }, []);

  const loadResults = useCallback((testId) => {
    setLoading(true);
    testAdminApi.getResults(testId || '', '')
      .then(setResults)
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadResults(filterTest); }, [filterTest, loadResults]);

  const filtered = results.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.studentName || '').toLowerCase().includes(s) || (r.registrationNo || '').toLowerCase().includes(s);
  });

  const subjectName = (id) => {
    const s = subjects.find(s => String(s.id) === String(id));
    return s ? s.name : '—';
  };

  const handleViewDetail = async (attemptId) => {
    if (openAttemptId === attemptId) { setOpenAttemptId(null); setDetail(null); return; }
    setOpenAttemptId(attemptId);
    setDetailLoading(true);
    setDetailErr('');
    try {
      const data = await testAdminApi.getResultDetail(attemptId);
      setDetail(data);
    } catch (e) { setDetailErr(e.message); }
    finally { setDetailLoading(false); }
  };

  const answerIcon = (status) => {
    if (status === 'CORRECT') return <span style={{ color: '#22863a', fontWeight: 700 }}>✓</span>;
    if (status === 'WRONG') return <span style={{ color: '#c53030', fontWeight: 700 }}>✗</span>;
    return <span style={{ color: '#a0aec0' }}>—</span>;
  };

  return (
    <div>
      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '12px 20px' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <select className="form-control" style={{ width: 200 }} value={filterTest} onChange={e => setFilterTest(e.target.value)}>
              <option value="">All Tests</option>
              {tests.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <div className="search-bar">
              <i className="fas fa-search"></i>
              <input type="text" placeholder="Search student name or reg no…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      {/* Results table */}
      <div className="card">
        <div className="card-header">
          <div className="card-title"><i className="fas fa-chart-bar" style={{marginRight:6,color:'#0066CC'}}></i>Results ({filtered.length})</div>
        </div>
        {err && <div className="error-msg" style={{ margin: '0 20px 16px' }}>{err}</div>}
        {loading ? <div className="loading-msg">Loading…</div> : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>#</th><th>👤 Student</th><th>🪪 Reg No</th><th>📝 Test</th><th>📖 Subject</th><th>🏫 Class</th>
                  <th>📊 Score</th><th>🔢 Total</th><th>%</th><th>🏅 Grade</th><th>🔘 Status</th><th>📅 Date</th><th>🔍 Detail</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={13} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No results found</td></tr>
                )}
                {filtered.map((r, i) => {
                  const pct = r.totalMarks ? Math.round((r.score / r.totalMarks) * 100) : 0;
                  return (
                    <>
                      <tr key={r.attemptId || i}>
                        <td>{i + 1}</td>
                        <td><strong>{r.studentName}</strong></td>
                        <td>{r.registrationNo || '—'}</td>
                        <td>{r.testName}</td>
                        <td>{subjectName(r.subjectId)}</td>
                        <td>{r.className || '—'}</td>
                        <td>{r.score}</td>
                        <td>{r.totalMarks}</td>
                        <td>{pct}%</td>
                        <td>
                          <span className="badge" style={gradeColor(r.grade)}>
                            {r.grade || '—'}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${r.status === 'PASSED' ? 'badge-green' : r.status === 'FAILED' ? 'badge-red' : 'badge-orange'}`}>
                            {r.status || '—'}
                          </span>
                        </td>
                        <td style={{ fontSize: 12, color: '#718096' }}>{r.submittedAt ? new Date(r.submittedAt).toLocaleDateString() : '—'}</td>
                        <td>
                          <button className="btn btn-outline btn-sm" onClick={() => handleViewDetail(r.attemptId)}>
                            {openAttemptId === r.attemptId ? '🔼 Hide' : '🔍 View'}
                          </button>
                        </td>
                      </tr>
                      {openAttemptId === r.attemptId && (
                        <tr key={`detail-${r.attemptId}`}>
                          <td colSpan={13} style={{ padding: 0, background: '#f7fafc' }}>
                            <div style={{ padding: 16 }}>
                              {detailLoading && <div className="loading-msg">Loading detail…</div>}
                              {detailErr && <div className="error-msg">{detailErr}</div>}
                              {detail && !detailLoading && (
                                <div className="table-wrap">
                                  <table>
                                    <thead>
                                      <tr><th>#</th><th>Question</th><th>Correct Ans</th><th>Student Ans</th><th>Result</th></tr>
                                    </thead>
                                    <tbody>
                                      {(detail.questions || []).map((q, qi) => (
                                        <tr key={qi}>
                                          <td>{qi + 1}</td>
                                          <td style={{ maxWidth: 280 }}>{truncate(q.questionText, 80)}</td>
                                          <td><strong>{q.correctOption}</strong></td>
                                          <td>{q.selectedOption || <span style={{ color: '#a0aec0' }}>—</span>}</td>
                                          <td>{answerIcon(q.answerStatus)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────

const TABS = ['Classes', 'Subjects', 'Questions', 'Tests', 'Results'];

export default function OnlineTests() {
  const [activeTab, setActiveTab] = useState('Classes');
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);

  const reloadSubjects = useCallback(() => {
    testAdminApi.getSubjects().then(setSubjects).catch(() => {});
  }, []);

  const reloadClasses = useCallback(() => {
    testAdminApi.getClasses().then(setClasses).catch(() => {});
  }, []);

  useEffect(() => { reloadSubjects(); reloadClasses(); }, [reloadSubjects, reloadClasses]);

  return (
    <div>
      <div className="tabs">
        {TABS.map(t => (
          <button key={t} className={`tab${activeTab === t ? ' active' : ''}`} onClick={() => {
            setActiveTab(t);
            if (t === 'Subjects') reloadSubjects();
            if (t === 'Classes') reloadClasses();
          }}>
            {t}
          </button>
        ))}
      </div>

      {activeTab === 'Classes'   && <ClassesTab />}
      {activeTab === 'Subjects'  && <SubjectsTab classes={classes} />}
      {activeTab === 'Questions' && <QuestionsTab subjects={subjects} classes={classes} />}
      {activeTab === 'Tests'     && <TestsTab subjects={subjects} classes={classes} />}
      {activeTab === 'Results'   && <ResultsTab subjects={subjects} />}
    </div>
  );
}
